/**
 * UTC date helpers. Everything on this page is UTC - the pickers are labeled
 * UTC, data is stored UTC, and the backend (pydantic) coerces unzoned strings
 * as UTC - so the picker string and the stored value represent the same
 * instant with no timezone math. See project memory.
 *
 * Ported verbatim from the legacy blob (static/index.html).
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** Midnight (00:00:00.000) of the given date, in UTC. */
export function startOfUtcDay(date: Date): Date {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a Date as "YYYY-MM-DDTHH:MM:SS" in UTC - the format expected by
 * <input type="datetime-local"> (unzoned) and accepted by the backend.
 */
export function fmtUtcDatetime(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
  );
}

/**
 * Accept legacy "YYYY-MM-DD" (from old URLs / ship metadata) and return the
 * datetime-local-compatible "YYYY-MM-DDT00:00:00".
 */
export function toDatetimeLocal(s: string | null | undefined): string {
  if (!s) return '';
  if (s.includes('T')) {
    // Already datetime - ensure it has seconds so the picker UI shows them.
    return s.length === 16 ? s + ':00' : s;
  }
  return s + 'T00:00:00';
}

/** `date` shifted by `days`, returned as a UTC datetime-local string. */
export function offsetDays(date: Date, days: number): string {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return fmtUtcDatetime(d);
}

/** `date` shifted by `months`, returned as a UTC datetime-local string. */
export function offsetMonths(date: Date, months: number): string {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + months);
  return fmtUtcDatetime(d);
}

/* ── Preset windows ─────────────────────────────────────── */

/** Rolling presets, in minutes back from now: the short presets of a rolling-window source, and `24h`. */
export const ROLLING_PRESET_MINUTES: Readonly<Record<string, number>> = {
  '5m': 5,
  '10m': 10,
  '30m': 30,
  '1h': 60,
  '6h': 360,
  '24h': 1440,
  // Under More on a rolling-window source.
  '15m': 15,
  '2h': 120,
  '3h': 180,
  '12h': 720,
  '48h': 2880,
};

/** `date` shifted by `months`, the day clamped to the target month's length. */
function shiftMonthsClamped(date: Date, months: number): Date {
  const d = new Date(date);
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return d;
}

/**
 * The concrete UTC window a named preset stands for at `now`, or null for the
 * two that are not arithmetic: `max` (a per-ship metadata lookup) and `custom`.
 *
 * Pure, and the one definition of every preset: the sidebar, a restored link
 * and a dashboard tile all resolve through it, so "Previous month" cannot mean
 * one thing in Explore and another on a tile.
 *
 * Day-based presets end at midnight *tomorrow* UTC, so today's data is always
 * inside the window. Calendar presets (`thisweek`, `mtd`, `ytd`) run from the
 * start of their period to that same end; the previous-period ones (`lastweek`,
 * `prevmonth`, `prevyear`) are the whole period before. Weeks start on Monday
 * (ISO 8601).
 */
export function presetWindow(preset: string, now: Date = new Date()): { start: string; end: string } | null {
  const minutes = ROLLING_PRESET_MINUTES[preset];
  if (minutes !== undefined) {
    return {
      start: fmtUtcDatetime(new Date(now.getTime() - minutes * 60_000)),
      end: fmtUtcDatetime(now),
    };
  }
  const today = startOfUtcDay(now);
  const tomorrow = new Date(today);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  const end = fmtUtcDatetime(tomorrow);
  const monday = new Date(today);
  monday.setUTCDate(monday.getUTCDate() - ((today.getUTCDay() + 6) % 7));
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const yearStart = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
  switch (preset) {
    case '7d': return { start: offsetDays(tomorrow, -7), end };
    case '30d': return { start: offsetDays(tomorrow, -30), end };
    case '1mo': return { start: fmtUtcDatetime(shiftMonthsClamped(tomorrow, -1)), end };
    case '3m': return { start: offsetMonths(tomorrow, -3), end };
    case '6m': return { start: offsetMonths(tomorrow, -6), end };
    case '1y': return { start: offsetMonths(tomorrow, -12), end };
    case 'today': return { start: fmtUtcDatetime(today), end };
    case 'yesterday': return { start: offsetDays(today, -1), end: fmtUtcDatetime(today) };
    case 'thisweek': return { start: fmtUtcDatetime(monday), end };
    case 'lastweek': return { start: offsetDays(monday, -7), end: fmtUtcDatetime(monday) };
    case 'mtd': return { start: fmtUtcDatetime(monthStart), end };
    case 'prevmonth': return { start: offsetMonths(monthStart, -1), end: fmtUtcDatetime(monthStart) };
    case 'ytd': return { start: fmtUtcDatetime(yearStart), end };
    case 'prevyear': return { start: offsetMonths(yearStart, -12), end: fmtUtcDatetime(yearStart) };
    default: return null;
  }
}

/** Recognize a fixed window for the picker's active chip without making its
 * dates roll. Assistant proposals and restored absolute dates have no named
 * preset; their duration can still be exactly one of the offered windows. */
export function matchingDatePreset<T extends string>(
  window: { start: string; end: string },
  presets: readonly T[],
  now: Date = new Date(),
): T | null {
  // Unzoned picker values are UTC, including date-only values from proposals.
  const utcMs = (value: string): number => Date.parse(
    /(?:Z|[+-]\d\d:?\d\d)$/i.test(value) ? value : `${toDatetimeLocal(value)}Z`,
  );
  const start = utcMs(window.start);
  const end = utcMs(window.end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  const matches = (candidate: { start: string; end: string } | null): boolean =>
    !!candidate && start === utcMs(candidate.start) && end === utcMs(candidate.end);

  // Calendar labels (Today, Previous month, ...) retain their current meaning.
  for (const preset of presets) {
    if (matches(presetWindow(preset, now))) return preset;
  }
  for (const preset of presets) {
    if (ROLLING_PRESET_MINUTES[preset] !== undefined) {
      if (matches(presetWindow(preset, new Date(end)))) return preset;
    } else if (['7d', '30d', '1mo', '3m', '6m', '1y'].includes(preset)) {
      // Resolve the span relative to its own end, including an assistant's
      // precise time of day, rather than snapping it to midnight tomorrow.
      const candidate = presetWindow(preset, new Date(end - 86_400_000))!;
      const shift = end - utcMs(candidate.end);
      if (start === utcMs(candidate.start) + shift) return preset;
    }
  }
  return null;
}

export type WindowShift = 'week' | 'month' | 'year' | 'day-last-week';

/**
 * The current window moved back in time - "same period last week / month /
 * year", or the single day a week before the window starts. Actions, not
 * presets: the result is fixed dates, not something that rolls with the clock.
 * Month and year shifts clamp the day (31 Mar back a month is 29 Feb or 28 Feb,
 * never 3 Mar). Null when the window does not parse.
 */
export function shiftWindow(
  window: { start: string; end: string },
  shift: WindowShift,
): { start: string; end: string } | null {
  const parse = (v: string): Date | null => {
    const ms = Date.parse(/(?:Z|[+-]\d\d:\d\d)$/i.test(v) ? v : `${v}Z`);
    return Number.isFinite(ms) ? new Date(ms) : null;
  };
  const start = parse(window.start);
  const end = parse(window.end);
  if (!start || !end) return null;
  switch (shift) {
    case 'week':
      return { start: offsetDays(start, -7), end: offsetDays(end, -7) };
    case 'month':
      return {
        start: fmtUtcDatetime(shiftMonthsClamped(start, -1)),
        end: fmtUtcDatetime(shiftMonthsClamped(end, -1)),
      };
    case 'year':
      return {
        start: fmtUtcDatetime(shiftMonthsClamped(start, -12)),
        end: fmtUtcDatetime(shiftMonthsClamped(end, -12)),
      };
    case 'day-last-week': {
      const day = startOfUtcDay(start);
      return { start: offsetDays(day, -7), end: offsetDays(day, -6) };
    }
  }
}

/** Human "time ago": compact by default, or full unit names for account dates. */
export function timeAgo(isoStr: string, style: 'short' | 'long' = 'short'): string {
  const now = Date.now();
  const then = new Date(isoStr).getTime();
  const diffSec = Math.floor((now - then) / 1000);
  if (diffSec < 60) return 'just now';
  const [count, unit, compact] = diffSec < 3600
    ? [Math.floor(diffSec / 60), 'minute', 'm'] as const
    : diffSec < 86400
      ? [Math.floor(diffSec / 3600), 'hour', 'h'] as const
      : [Math.floor(diffSec / 86400), 'day', 'd'] as const;
  return style === 'long'
    ? `${count} ${unit}${count === 1 ? '' : 's'} ago`
    : `${count}${compact} ago`;
}

/**
 * How each date preset reads in prose - on the dashboard toolbar, on a catalog
 * row, anywhere a stored range has to be described rather than edited.
 *
 * Lives here rather than in one feature because three of them now need the same
 * words, and two spellings of "Last 30 days" is how a UI starts to feel like
 * two UIs.
 */
export const PRESET_LABELS: Record<string, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  '1y': 'Last 1 year',
  '1mo': 'Last 1 month',
  today: 'Today',
  yesterday: 'Yesterday',
  thisweek: 'This week',
  lastweek: 'Last week',
  mtd: 'Month to date',
  prevmonth: 'Previous month',
  ytd: 'Year to date',
  prevyear: 'Previous year',
  max: 'All data',
  '5m': 'Last 5 min',
  '10m': 'Last 10 min',
  '30m': 'Last 30 min',
  '1h': 'Last 1 hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '15m': 'Last 15 min',
  '2h': 'Last 2 hours',
  '3h': 'Last 3 hours',
  '12h': 'Last 12 hours',
  '48h': 'Last 48 hours',
};

/** `2026-09-03T12:00:00` -> `3 Sep 2026`. The time is noise on a summary. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '?';
  const date = new Date(`${value.slice(0, 19)}Z`);
  if (Number.isNaN(date.getTime())) return value.slice(0, 10);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
